import { useEffect, useMemo, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Loader2, GanttChartSquare, ClipboardList, Calendar, Filter, AlertTriangle, Users, Settings2, Columns3, FileStack, Save, Check, Circle, ChevronDown, ChevronRight, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import GanttChart from "@/components/plan/GanttChart";
import ResourceView from "@/components/plan/ResourceView";
import KanbanBoard from "@/components/plan/KanbanBoard";
import PlanTaskDialog from "@/components/plan/PlanTaskDialog";
import ConflictBanner from "@/components/plan/ConflictBanner";
import WorkingCalendarDialog from "@/components/plan/WorkingCalendarDialog";
import SaveAsTemplateDialog from "@/components/plan/SaveAsTemplateDialog";
import ApplyTemplateDialog from "@/components/plan/ApplyTemplateDialog";
import { dueDateStatus, isAtRisk, dueBufferDays } from "@/components/plan/dueDateStatus";
import { initials, colorFor } from "@/lib/avatar";
import { taskCommentsApi } from "@/lib/api";
import { MessageSquare } from "lucide-react";
import { plansApi, projectMembersApi } from "@/lib/api";
import { api as axiosClient } from "@/lib/api";
import { formatDuration } from "@/lib/duration";
import { useAuth } from "@/lib/auth";

const PHASE_PILL = {
  pre:  { label: "Pre",  bg: "bg-teal-500/15 text-teal-300 border border-teal-500/40" },
  prod: { label: "Prod", bg: "bg-amber-500/15 text-amber-300 border border-amber-500/40" },
  post: { label: "Post", bg: "bg-violet-500/15 text-violet-300 border border-violet-500/40" },
};

const DAY_LABELS = { 1: "Mon", 2: "Tue", 3: "Wed", 4: "Thu", 5: "Fri", 6: "Sat", 7: "Sun" };

export default function ProjectPlanPage() {
  const { project } = useOutletContext();
  const { user } = useAuth();
  const projectId = project.id;
  const canEdit = project.my_role === "admin" || project.my_role === "editor";
  const isAdmin = user?.role === "admin";

  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [dependencies, setDependencies] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [allocations, setAllocations] = useState({});
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [phaseFilter, setPhaseFilter] = useState("all");
  const [myTasksOnly, setMyTasksOnly] = useState(() => {
    try { return localStorage.getItem("plan:myTasksOnly") === "1"; } catch { return false; }
  });
  const toggleMyTasks = () => {
    setMyTasksOnly((prev) => {
      const next = !prev;
      try { localStorage.setItem("plan:myTasksOnly", next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  };
  const [view, setView] = useState("timeline"); // "timeline" | "board" | "resources"
  const [dialogOpen, setDialogOpen] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [applyTemplateOpen, setApplyTemplateOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [defaultPhase, setDefaultPhase] = useState("pre");
  const [searchParams, setSearchParams] = useSearchParams();

  // Auto-open a task dialog when the URL contains ?task=<id>. Used by the
  // Discussion rollup page to deep-link a comment back to its task. We clear
  // the param after opening so refresh doesn't loop.
  useEffect(() => {
    const wantedId = searchParams.get("task");
    if (!wantedId || !tasks?.length) return;
    const found = tasks.find((t) => t.id === wantedId);
    if (found) {
      setEditing(found);
      setDialogOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("task");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, tasks, setSearchParams]);

  const [commentCounts, setCommentCounts] = useState({});

  const load = async () => {
    try {
      const [data, users, counts] = await Promise.all([
        plansApi.get(projectId),
        // Fetch assignable users (members + global admins)
        fetchAssignable(projectId),
        taskCommentsApi.counts(projectId).catch(() => ({})),
      ]);
      setPlan(data.plan);
      setTasks(data.tasks || []);
      setDependencies(data.dependencies || []);
      setConflicts(data.conflicts || []);
      setAllocations(data.allocations || {});
      setAssignableUsers(users || []);
      setCommentCounts(counts || {});
    } catch {
      toast.error("Failed to load plan");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    load();
  }, [projectId]);

  const filteredTasks = useMemo(() => {
    let out = tasks;
    if (myTasksOnly && user?.id) {
      out = out.filter((t) => t.assigned_user_id === user.id);
    }
    if (phaseFilter !== "all") {
      out = out.filter((t) => t.phase === phaseFilter);
    }
    return out;
  }, [tasks, phaseFilter, myTasksOnly, user?.id]);

  // Are any tasks assigned to the current user? Used to hide the "My Tasks"
  // chip on projects where the user has nothing — avoids a confusing empty toggle.
  const hasAnyOwnTasks = useMemo(() => {
    if (!user?.id) return false;
    return tasks.some((t) => t.assigned_user_id === user.id);
  }, [tasks, user?.id]);

  const conflictingTaskIds = useMemo(
    () => new Set((conflicts || []).map((c) => c.task_id)),
    [conflicts],
  );

  const stats = useMemo(() => {
    const out = { pre: 0, prod: 0, post: 0, total: tasks.length, done: 0 };
    tasks.forEach((t) => {
      if (out[t.phase] !== undefined) out[t.phase] += 1;
      if (t.kanban_status === "done") out.done += 1;
    });
    out.percent = out.total > 0 ? Math.round((out.done / out.total) * 100) : 0;
    return out;
  }, [tasks]);

  // Count over-allocated days across all assignees for the stat strip
  const overAllocStat = useMemo(() => {
    const hoursPerDay = plan?.working_hours_per_day || 8;
    let count = 0;
    let people = new Set();
    Object.entries(allocations).forEach(([uid, days]) => {
      let found = false;
      Object.values(days).forEach((h) => { if (h > hoursPerDay) { count += 1; found = true; } });
      if (found) people.add(uid);
    });
    return { count, peopleCount: people.size };
  }, [allocations, plan?.working_hours_per_day]);

  // Helper: refetch plan after backend mutations that change allocations
  const refetchAllocations = async () => {
    try {
      const data = await plansApi.get(projectId);
      setAllocations(data.allocations || {});
    } catch {
      // silent — UI still works without it
    }
  };

  // Build a toast for leveled / reverted moves
  const showLeveledToast = (movesArray) => {
    const moves = Array.isArray(movesArray) ? movesArray : [movesArray];
    if (!moves.length) return;
    const label =
      moves.length === 1
        ? `"${moves[0].name}" auto-leveled to ${moves[0].new_start}`
        : `${moves.length} tasks auto-leveled to clear over-allocation`;
    toast(label, {
      duration: 10000,
      action: {
        label: "Undo",
        onClick: async () => {
          try {
            const resp = await plansApi.revertMoves(
              projectId,
              moves.map((m) => ({ task_id: m.task_id, start: m.old_start, end: m.old_end })),
            );
            // Apply the reverted tasks back into local state
            const byId = new Map((resp.reverted_tasks || []).map((t) => [t.id, t]));
            setTasks((prev) => prev.map((t) => byId.get(t.id) || t));
            setConflicts(resp.conflicts || []);
            refetchAllocations();
            toast.success("Reverted");
          } catch {
            toast.error("Failed to undo");
          }
        },
      },
    });
  };

  // Merge cascade response into local state without refetching
  const applyCascadeResponse = (resp) => {
    const updates = new Map();
    if (resp?.task) updates.set(resp.task.id, resp.task);
    (resp?.cascaded_tasks || []).forEach((t) => updates.set(t.id, t));
    if (updates.size > 0) {
      setTasks((prev) => prev.map((t) => updates.get(t.id) || t));
      refetchAllocations();
    }
    if (Array.isArray(resp?.conflicts)) setConflicts(resp.conflicts);
    if (resp?.leveled) showLeveledToast(resp.leveled);
    // Phase 1.3.2: surgical cascade re-fit notification
    const refits = resp?.cascade_refits || [];
    if (refits.length > 0) {
      showCascadeRefitToast(refits);
    }
  };

  // Phase 1.3.2 — Cascade re-fit toast: lets the user understand that
  // downstream tasks were both cascaded AND auto-leveled.
  const showCascadeRefitToast = (refits) => {
    const moves = Array.isArray(refits) ? refits : [refits];
    if (!moves.length) return;
    const label =
      moves.length === 1
        ? `Cascade re-fit: "${moves[0].name}" bumped to ${moves[0].new_start} to clear overallocation`
        : `Cascade re-fit: ${moves.length} downstream tasks bumped to clear overallocation`;
    toast(label, {
      duration: 10000,
      action: {
        label: "Undo",
        onClick: async () => {
          try {
            const resp = await plansApi.revertMoves(
              projectId,
              moves.map((m) => ({ task_id: m.task_id, start: m.old_start, end: m.old_end })),
            );
            const byId = new Map((resp.reverted_tasks || []).map((t) => [t.id, t]));
            setTasks((prev) => prev.map((t) => byId.get(t.id) || t));
            setConflicts(resp.conflicts || []);
            refetchAllocations();
            toast.success("Cascade re-fit reverted");
          } catch {
            toast.error("Failed to undo");
          }
        },
      },
    });
  };

  // ── Handlers ──────────────────────────────────────────────────────────────
  const openCreate = (phase = "pre") => {
    setEditing(null);
    setDefaultPhase(phase);
    setDialogOpen(true);
  };

  const openEdit = (task) => {
    setEditing(task);
    setDefaultPhase(task.phase);
    setDialogOpen(true);
  };

  const handleSave = async (data) => {
    try {
      if (editing) {
        const wasAtRisk = isAtRisk(editing);
        const resp = await plansApi.updateTask(editing.id, data);
        applyCascadeResponse(resp);
        // If the task was at-risk before and is no longer at-risk after the
        // save, celebrate the producer's fix with a one-shot toast. Same
        // predicate the backend uses for the Launchpad At-Risk widget.
        const updated = resp?.task;
        if (wasAtRisk && updated && !isAtRisk(updated)) {
          if (updated.kanban_status === "done") {
            toast.success("Marked complete · cleared from At-Risk");
          } else if (!updated.due_date) {
            toast.success("Cleared from At-Risk · no deadline");
          } else {
            const buffer = dueBufferDays(updated);
            if (buffer !== null && buffer > 0) {
              toast.success(
                `Cleared from At-Risk · ${buffer} day${buffer === 1 ? "" : "s"} buffer`,
              );
            } else {
              toast.success("Cleared from At-Risk");
            }
          }
        } else {
          toast.success("Task updated");
        }
      } else {
        const resp = await plansApi.createTask(projectId, data);
        // Phase 1.3.1: create response is now {task, leveled}. New in
        // Phase 1.5.2: also carries {dependencies, cascaded_tasks,
        // skipped_predecessors} when the client attached predecessors on
        // create.
        const created = resp?.task || resp;
        setTasks((prev) => {
          const withNew = [...prev, created];
          const casc = resp?.cascaded_tasks || [];
          if (!casc.length) return withNew;
          const byId = new Map(casc.map((t) => [t.id, t]));
          return withNew.map((t) => byId.get(t.id) || t);
        });
        if (Array.isArray(resp?.dependencies) && resp.dependencies.length) {
          setDependencies((prev) => [...prev, ...resp.dependencies]);
        }
        refetchAllocations();
        if (resp?.leveled) showLeveledToast(resp.leveled);
        else toast.success("Task created");
        // Warn about any predecessor links that couldn't be created.
        const skipped = resp?.skipped_predecessors || [];
        if (skipped.length) {
          toast.warning(
            `${skipped.length} predecessor${skipped.length === 1 ? "" : "s"} skipped · ${skipped[0].reason}`,
          );
        }
      }
      setDialogOpen(false);
      setEditing(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save task");
    }
  };

  const handleLevelPlan = async () => {
    if (!canEdit) return;
    try {
      const resp = await plansApi.levelPlan(projectId);
      const moves = resp?.moves || [];
      if (moves.length === 0) {
        toast("No tasks needed leveling");
        return;
      }
      // Apply moved tasks locally
      const byId = new Map((resp.moved_tasks || []).map((t) => [t.id, t]));
      setTasks((prev) => prev.map((t) => byId.get(t.id) || t));
      setConflicts(resp.conflicts || []);
      refetchAllocations();
      showLeveledToast(moves);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to level plan");
    }
  };

  const handleDelete = async (task) => {
    try {
      await plansApi.removeTask(task.id);
      setTasks((prev) => prev.filter((t) => t.id !== task.id));
      setDependencies((prev) =>
        prev.filter((d) => d.from_id !== task.id && d.to_id !== task.id),
      );
      const conf = await plansApi.listConflicts(projectId);
      setConflicts(conf.conflicts || []);
      refetchAllocations();
      setDialogOpen(false);
      setEditing(null);
      toast.success("Task deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  // Admin-only bulk delete, invoked from the collapsible Completed section
  // in TaskTable below. Loops the existing single-task delete endpoint via
  // Promise.allSettled so the auto-managed-production safeguard on the
  // server (`DELETE /api/plan-tasks/:id` 400s on production blocks still
  // linked to a call sheet) still applies per-task — we surface any such
  // rejections in the toast instead of hiding them.
  const handleBulkDeleteCompleted = async (ids) => {
    if (!ids?.length) return { ok: 0, failed: 0 };
    const results = await Promise.allSettled(
      ids.map((id) => plansApi.removeTask(id)),
    );
    const okIds = [];
    let failed = 0;
    results.forEach((r, i) => {
      if (r.status === "fulfilled") okIds.push(ids[i]);
      else failed += 1;
    });
    if (okIds.length) {
      const okSet = new Set(okIds);
      setTasks((prev) => prev.filter((t) => !okSet.has(t.id)));
      setDependencies((prev) =>
        prev.filter((d) => !okSet.has(d.from_id) && !okSet.has(d.to_id)),
      );
      try {
        const conf = await plansApi.listConflicts(projectId);
        setConflicts(conf.conflicts || []);
      } catch { /* non-fatal */ }
      refetchAllocations();
    }
    if (failed === 0) {
      toast.success(`${okIds.length} completed task${okIds.length === 1 ? "" : "s"} deleted`);
    } else if (okIds.length === 0) {
      toast.error("Couldn't delete the selected tasks");
    } else {
      toast.warning(
        `${okIds.length} deleted · ${failed} couldn't be removed (auto-synced from a call sheet)`,
      );
    }
    return { ok: okIds.length, failed };
  };


  const handleToggleLock = async (task) => {
    if (!canEdit) return;
    const next = task.schedule_mode === "locked" ? "cascade" : "locked";
    try {
      const resp = await plansApi.updateTask(task.id, { schedule_mode: next });
      applyCascadeResponse(resp);
    } catch {
      toast.error("Failed to toggle lock");
    }
  };

  const handleMoveTask = async (task, newStart) => {
    if (!canEdit) return;
    try {
      const resp = await plansApi.updateTask(task.id, { start: newStart });
      applyCascadeResponse(resp);
      const cascadedCount = resp?.cascaded_tasks?.length || 0;
      if (cascadedCount > 0) {
        toast.success(`Moved "${task.name}" to ${newStart} — ${cascadedCount} downstream task${cascadedCount === 1 ? "" : "s"} cascaded`);
      } else {
        toast.success(`Moved "${task.name}" to ${newStart}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to move task");
    }
  };

  const handleCreateDependency = async (fromId, toId, type = "FS") => {
    if (!canEdit) return;
    try {
      const resp = await plansApi.createDependency(projectId, fromId, toId, type);
      setDependencies((prev) => [...prev, resp.dependency]);
      applyCascadeResponse(resp);
      toast.success(`${type} dependency added`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add dependency");
    }
  };

  const handleDeleteDependency = async (dep) => {
    if (!canEdit) return;
    try {
      const resp = await plansApi.removeDependency(dep.id);
      setDependencies((prev) => prev.filter((d) => d.id !== dep.id));
      if (Array.isArray(resp.conflicts)) setConflicts(resp.conflicts);
      toast.success("Dependency removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove dependency");
    }
  };

  const handleDismissConflict = async (conflict) => {
    try {
      await plansApi.dismissConflict(conflict.id);
      setConflicts((prev) => prev.filter((c) => c.id !== conflict.id));
    } catch {
      toast.error("Failed to dismiss");
    }
  };

  const handleResolveConflict = async (conflict, action) => {
    if (!canEdit) return;
    try {
      const resp = await plansApi.resolveConflict(conflict.id, action);
      applyCascadeResponse({
        cascaded_tasks: resp.cascaded_tasks,
        conflicts: resp.conflicts,
      });
      const verb = action === "move_forward" ? "moved forward" : "unlocked";
      toast.success(`Conflict resolved — task ${verb}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to resolve");
    }
  };

  const handleSaveCalendar = async (payload) => {
    try {
      const updated = await plansApi.updatePlan(projectId, payload);
      setPlan(updated);
      refetchAllocations();
      toast.success("Calendar saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save calendar");
    }
  };

  // Tier 1 — toggle task completion (kanban_status done <-> todo)
  const handleToggleComplete = async (task) => {
    if (!canEdit) return;
    const next = task.kanban_status === "done" ? "todo" : "done";
    const wasAtRisk = isAtRisk(task);
    // Optimistic update
    setTasks((prev) =>
      prev.map((t) => (t.id === task.id ? { ...t, kanban_status: next } : t)),
    );
    try {
      const resp = await plansApi.updateTask(task.id, { kanban_status: next });
      applyCascadeResponse(resp);
      // Completing an at-risk task is the most satisfying clear — give it a
      // dedicated message so the producer knows the system noticed.
      if (next === "done" && wasAtRisk) {
        toast.success(`"${task.name}" complete · cleared from At-Risk`);
      } else {
        toast.success(next === "done" ? `"${task.name}" marked complete` : `"${task.name}" reopened`);
      }
    } catch (e) {
      // Revert
      setTasks((prev) =>
        prev.map((t) => (t.id === task.id ? { ...t, kanban_status: task.kanban_status } : t)),
      );
      toast.error(e?.response?.data?.detail || "Failed to update task");
    }
  };

  // Phase 1.5 — Kanban drop handler: change phase only, no date math.
  const handleChangePhase = async (task, newPhase) => {
    if (!canEdit) return;
    if (task.phase === "prod" && task.call_sheet_id) {
      toast.error("Production shoot days are anchored to call sheets and cannot be re-phased");
      return;
    }
    const oldPhase = task.phase;
    // Optimistic update
    setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, phase: newPhase } : t)));
    try {
      const resp = await plansApi.updateTask(task.id, { phase: newPhase });
      applyCascadeResponse(resp);
      const labels = { pre: "Pre", prod: "Production", post: "Post" };
      toast.success(`Moved "${task.name}" to ${labels[newPhase]}`);
    } catch (e) {
      // Revert
      setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, phase: oldPhase } : t)));
      toast.error(e?.response?.data?.detail || "Failed to change phase");
    }
  };

  // Phase 1.6 — Apply template handler: refresh plan after successful apply.
  const handleAfterTemplateApplied = async () => {
    setLoading(true);
    await load();
  };

  // Default anchor date used by Save/Apply template dialogs.
  // Fallback order (matches backend convention): project.start_date →
  // earliest production task start → earliest task start → today.
  const defaultAnchorDate = useMemo(() => {
    if (project?.start_date) return project.start_date;
    const prodTasks = tasks.filter((t) => t.phase === "prod" && t.start);
    if (prodTasks.length > 0) {
      return prodTasks.reduce((min, t) => (t.start < min ? t.start : min), prodTasks[0].start);
    }
    if (tasks.length > 0) {
      const sorted = [...tasks].filter((t) => t.start).sort((a, b) => a.start.localeCompare(b.start));
      if (sorted.length > 0) return sorted[0].start;
    }
    return new Date().toISOString().slice(0, 10);
  }, [tasks, project?.start_date]);

  if (loading) {
    return (
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-16 flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-amber-500 animate-spin" />
      </div>
    );
  }

  const workingDaysLabel = (plan?.working_days || [1, 2, 3, 4, 5])
    .map((d) => DAY_LABELS[d])
    .join("–");

  return (
    <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-10" data-testid="project-plan-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-overline text-amber-500/80 mb-2">Project Plan</div>
          <h2 className="font-heading text-3xl font-light text-zinc-100">
            Production Timeline
          </h2>
          <p className="text-sm text-zinc-500 mt-2 max-w-xl">
            Three swimlanes: Pre-Production → Production → Post-Production. Switch to
            Resources to see who is loaded on what.
          </p>
        </div>
        {canEdit && (
          <Button
            data-testid="add-task-btn"
            onClick={() => openCreate("pre")}
            className="bg-amber-500 text-black hover:bg-amber-400 rounded-sm"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            Add Task
          </Button>
        )}
      </div>

      <ConflictBanner
        conflicts={conflicts}
        tasks={tasks}
        onDismiss={canEdit ? handleDismissConflict : null}
        onResolve={canEdit ? handleResolveConflict : null}
      />

      {/* Stat strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
        <StatCard
          label="Total Tasks"
          value={stats.total}
          icon={ClipboardList}
          sub={stats.total > 0 ? `${stats.done} done · ${stats.percent}%` : null}
          testid="stat-total"
        />
        <StatCard label="Pre-Production" value={stats.pre} dot="bg-teal-500" />
        <StatCard label="Production" value={stats.prod} dot="bg-amber-500" />
        {overAllocStat.count > 0 ? (
          <StatCard
            label="Over-Allocated Days"
            value={overAllocStat.count}
            warn
            sub={`${overAllocStat.peopleCount} person${overAllocStat.peopleCount === 1 ? "" : "s"}`}
            testid="stat-overalloc"
          />
        ) : (
          <StatCard label="Post-Production" value={stats.post} dot="bg-violet-500" />
        )}
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="inline-flex rounded-sm border border-zinc-800 overflow-hidden" data-testid="view-toggle">
            <button
              type="button"
              data-testid="view-timeline"
              onClick={() => setView("timeline")}
              className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wide transition-colors ${
                view === "timeline" ? "bg-amber-500 text-black" : "bg-zinc-950 text-zinc-400 hover:text-zinc-100"
              }`}
            >
              <GanttChartSquare className="w-3 h-3 inline mr-1.5" />
              Timeline
            </button>
            <button
              type="button"
              data-testid="view-board"
              onClick={() => setView("board")}
              className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wide transition-colors border-l border-zinc-800 ${
                view === "board" ? "bg-amber-500 text-black" : "bg-zinc-950 text-zinc-400 hover:text-zinc-100"
              }`}
            >
              <Columns3 className="w-3 h-3 inline mr-1.5" />
              Board
            </button>
            <button
              type="button"
              data-testid="view-resources"
              onClick={() => setView("resources")}
              className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wide transition-colors border-l border-zinc-800 ${
                view === "resources" ? "bg-amber-500 text-black" : "bg-zinc-950 text-zinc-400 hover:text-zinc-100"
              }`}
            >
              <Users className="w-3 h-3 inline mr-1.5" />
              Resources
            </button>
          </div>

          {view === "timeline" && (
            <>
              {hasAnyOwnTasks && (
                <button
                  type="button"
                  data-testid="plan-my-tasks-toggle"
                  onClick={toggleMyTasks}
                  aria-pressed={myTasksOnly}
                  title={myTasksOnly ? "Showing only your tasks" : "Show only tasks assigned to you"}
                  className={`ml-2 inline-flex items-center gap-1.5 h-8 px-3 rounded-sm text-xs font-mono uppercase tracking-wide transition-colors border ${
                    myTasksOnly
                      ? "bg-amber-500/15 border-amber-500/50 text-amber-300"
                      : "bg-zinc-950 border-zinc-800 text-zinc-400 hover:text-amber-400 hover:border-zinc-700"
                  }`}
                >
                  <Users className="w-3.5 h-3.5" />
                  My Tasks
                </button>
              )}
              <Filter className="w-3.5 h-3.5 text-zinc-500 ml-2" />
              <Select value={phaseFilter} onValueChange={setPhaseFilter}>
                <SelectTrigger
                  data-testid="plan-phase-filter"
                  className="w-40 bg-zinc-950 border-zinc-800 focus:ring-amber-500"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                  <SelectItem value="all" className="focus:bg-zinc-900">All Phases</SelectItem>
                  <SelectItem value="pre" className="focus:bg-zinc-900">Pre-Production</SelectItem>
                  <SelectItem value="prod" className="focus:bg-zinc-900">Production</SelectItem>
                  <SelectItem value="post" className="focus:bg-zinc-900">Post-Production</SelectItem>
                </SelectContent>
              </Select>
            </>
          )}

          {overAllocStat.count > 0 && canEdit && (
            <Button
              type="button"
              size="sm"
              data-testid="level-plan-btn"
              onClick={handleLevelPlan}
              className="bg-orange-500 text-black hover:bg-orange-400 rounded-sm h-8 px-3 ml-1"
            >
              <AlertTriangle className="w-3.5 h-3.5 mr-1.5" />
              Level Plan
            </Button>
          )}

          {/* Phase 1.6 — Plan Templates */}
          {canEdit && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              data-testid="apply-template-open-btn"
              onClick={() => setApplyTemplateOpen(true)}
              className="border-zinc-800 text-zinc-300 hover:text-amber-500 hover:border-zinc-700 rounded-sm h-8 px-3 ml-1"
            >
              <FileStack className="w-3.5 h-3.5 mr-1.5" />
              Apply Template
            </Button>
          )}
          {isAdmin && tasks.length > 0 && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              data-testid="save-template-open-btn"
              onClick={() => setSaveTemplateOpen(true)}
              className="border-zinc-800 text-zinc-300 hover:text-amber-500 hover:border-zinc-700 rounded-sm h-8 px-3"
            >
              <Save className="w-3.5 h-3.5 mr-1.5" />
              Save as Template
            </Button>
          )}
        </div>

        <div className="flex items-center gap-2 text-xs text-zinc-500">
          <button
            type="button"
            data-testid="calendar-settings-btn"
            onClick={() => setCalendarOpen(true)}
            disabled={!canEdit}
            className="inline-flex items-center gap-1.5 hover:text-amber-500 transition-colors disabled:opacity-50"
            title="Edit working calendar"
          >
            <Calendar className="w-3 h-3" />
            <span>
              {workingDaysLabel} · {plan?.working_hours_per_day || 8}h/day
              {(plan?.holidays?.length || 0) > 0 && ` · ${plan.holidays.length} holidays`}
            </span>
            <Settings2 className="w-3 h-3" />
          </button>
        </div>
      </div>

      {tasks.length === 0 ? (
        <EmptyPlan canEdit={canEdit} onCreate={() => openCreate("pre")} />
      ) : view === "timeline" ? (
        <>
          <GanttChart
            plan={plan}
            tasks={filteredTasks}
            dependencies={dependencies}
            conflictingTaskIds={conflictingTaskIds}
            members={assignableUsers}
            allocations={allocations}
            onTaskClick={openEdit}
            onToggleLock={handleToggleLock}
            onToggleComplete={handleToggleComplete}
            onMoveTask={handleMoveTask}
            onCreateDependency={handleCreateDependency}
            onDeleteDependency={handleDeleteDependency}
            readonly={!canEdit}
          />
          <TaskTable
            tasks={filteredTasks}
            onEdit={openEdit}
            canEdit={canEdit}
            conflictingTaskIds={conflictingTaskIds}
            onToggleComplete={handleToggleComplete}
            onBulkDeleteCompleted={handleBulkDeleteCompleted}
            commentCounts={commentCounts}
            members={assignableUsers}
            currentUserId={user?.id}
            currentUserRole={user?.role}
          />
        </>
      ) : view === "board" ? (
        <KanbanBoard
          tasks={tasks}
          members={assignableUsers.map((u) => ({ id: u.user_id, ...u }))}
          conflictingTaskIds={conflictingTaskIds}
          onTaskClick={openEdit}
          onChangePhase={handleChangePhase}
          readonly={!canEdit}
        />
      ) : (
        <ResourceView
          plan={plan}
          tasks={tasks}
          members={assignableUsers}
          allocations={allocations}
          onTaskClick={openEdit}
        />
      )}

      <PlanTaskDialog
        open={dialogOpen}
        onOpenChange={(o) => { setDialogOpen(o); if (!o) setEditing(null); }}
        task={editing}
        projectId={projectId}
        canEdit={canEdit}
        defaultPhase={defaultPhase}
        allTasks={tasks}
        dependencies={dependencies}
        assignableUsers={assignableUsers}
        onSave={handleSave}
        onDelete={handleDelete}
        onCreateDependency={handleCreateDependency}
        onDeleteDependency={handleDeleteDependency}
      />

      <WorkingCalendarDialog
        open={calendarOpen}
        onOpenChange={setCalendarOpen}
        plan={plan}
        onSave={handleSaveCalendar}
      />

      <SaveAsTemplateDialog
        open={saveTemplateOpen}
        onOpenChange={setSaveTemplateOpen}
        projectId={projectId}
        defaultAnchorDate={defaultAnchorDate}
      />

      <ApplyTemplateDialog
        open={applyTemplateOpen}
        onOpenChange={setApplyTemplateOpen}
        projectId={projectId}
        defaultAnchorDate={defaultAnchorDate}
        isAdmin={isAdmin}
        onApplied={handleAfterTemplateApplied}
      />
    </div>
  );
}

// Helper: fetch members + admin list via the new assignable-users endpoint
async function fetchAssignable(projectId) {
  try {
    const res = await axiosClient.get(`/projects/${projectId}/assignable-users`);
    return res.data;
  } catch {
    try {
      const members = await projectMembersApi.list(projectId);
      return (members || []).map((m) => ({
        user_id: m.user_id,
        email: m.email,
        name: m.name,
        is_global_admin: false,
      }));
    } catch {
      return [];
    }
  }
}

function StatCard({ label, value, icon: Icon, dot, warn, sub, testid }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-md border p-4 ${
        warn ? "border-orange-900/60 bg-orange-950/20" : "border-zinc-800 bg-zinc-950/60"
      }`}
    >
      <div className="flex items-center gap-2 text-overline mb-2">
        {dot && <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />}
        {Icon && <Icon className={`w-3 h-3 ${warn ? "text-orange-400" : "text-zinc-500"}`} />}
        {warn && <AlertTriangle className="w-3 h-3 text-orange-400" />}
        <span className={warn ? "text-orange-400" : "text-zinc-500"}>{label}</span>
      </div>
      <div className={`font-mono text-2xl ${warn ? "text-orange-300" : "text-zinc-100"}`}>
        {value}
      </div>
      {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}

function EmptyPlan({ canEdit, onCreate }) {
  return (
    <div
      data-testid="plan-empty-state"
      className="border border-dashed border-zinc-800 rounded-md p-16 text-center"
    >
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-sm bg-zinc-900 border border-zinc-800 mb-5">
        <GanttChartSquare className="w-7 h-7 text-amber-500" strokeWidth={1.5} />
      </div>
      <h3 className="font-heading text-2xl font-light text-zinc-100 mb-2">
        No plan yet
      </h3>
      <p className="text-zinc-500 mb-6 text-sm max-w-md mx-auto">
        Add the first task to start scheduling Pre-Production. Shoot days will appear in the
        Production lane as you add Call Sheet days.
      </p>
      {canEdit && (
        <Button
          data-testid="plan-empty-create-btn"
          onClick={onCreate}
          className="bg-amber-500 text-black hover:bg-amber-400 rounded-sm"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Add First Task
        </Button>
      )}
    </div>
  );
}

function TaskTable({
  tasks,
  onEdit,
  canEdit,
  conflictingTaskIds,
  onToggleComplete,
  onBulkDeleteCompleted,
  commentCounts = {},
  members = [],
  currentUserId,
  currentUserRole,
}) {
  // Visibility: admin/editor see assignees on every task; viewer only sees
  // assignee chip when the task is assigned to them.
  const canSeeAllAssignees =
    currentUserRole === "admin" || currentUserRole === "editor";
  const membersById = members.reduce((acc, m) => {
    const id = m.user_id || m.id;
    if (id) acc[id] = m;
    return acc;
  }, {});

  // Partition into active + done. The input `tasks` is already filtered by
  // phase + assignee upstream (`filteredTasks`) so we keep that respected
  // and just split on `kanban_status`.
  const activeTasks = tasks.filter((t) => t.kanban_status !== "done");
  const doneTasks = tasks.filter((t) => t.kanban_status === "done");

  // Completed section: ALWAYS defaults to collapsed on every mount per spec
  // — intentionally not persisted across navigations.
  const [completedOpen, setCompletedOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const isAdminRole = currentUserRole === "admin";

  // Reset selection whenever the pool of done tasks changes (e.g. after a
  // bulk delete removes rows, or after a toggle flips a task back to todo).
  const doneIdsKey = doneTasks.map((t) => t.id).join("|");
  useEffect(() => {
    setSelectedIds(new Set());
  }, [doneIdsKey]);

  // Hooks above — safe to short-circuit now.
  if (!tasks.length) return null;

  // Collapsing the section discards any in-flight selection so re-opening
  // starts clean (matches the "always default collapsed" intent).
  const toggleCompleted = () => {
    setCompletedOpen((prev) => {
      if (prev) setSelectedIds(new Set());
      return !prev;
    });
  };

  const toggleRowSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allSelected =
    doneTasks.length > 0 && selectedIds.size === doneTasks.length;
  const someSelected = selectedIds.size > 0 && !allSelected;
  const toggleSelectAll = () => {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(doneTasks.map((t) => t.id)));
  };

  const confirmBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    setDeleting(true);
    try {
      await onBulkDeleteCompleted?.(ids);
    } finally {
      setDeleting(false);
      setConfirmOpen(false);
      setSelectedIds(new Set());
    }
  };

  const renderRow = (t, { withCheckbox = false } = {}) => {
    const pill = PHASE_PILL[t.phase] || PHASE_PILL.pre;
    const inConflict = conflictingTaskIds?.has(t.id);
    const isDone = t.kanban_status === "done";
    const checked = selectedIds.has(t.id);
    return (
      <div
        key={t.id}
        data-testid={`task-row-${t.id}`}
        className={`w-full px-5 py-3 flex items-center gap-4 hover:bg-zinc-950 transition-colors ${
          inConflict ? "bg-rose-950/20" : ""
        } ${isDone ? "opacity-60" : ""}`}
      >
        {withCheckbox && (
          <span
            onClick={(e) => e.stopPropagation()}
            className="shrink-0 flex items-center"
          >
            <Checkbox
              data-testid={`plan-completed-select-${t.id}`}
              checked={checked}
              onCheckedChange={() => toggleRowSelected(t.id)}
              aria-label={`Select "${t.name}" for bulk delete`}
              className="border-zinc-700 data-[state=checked]:bg-rose-500 data-[state=checked]:border-rose-500"
            />
          </span>
        )}
        <button
          type="button"
          data-testid={`task-row-complete-${t.id}`}
          onClick={(e) => {
            e.stopPropagation();
            if (canEdit && onToggleComplete) onToggleComplete(t);
          }}
          disabled={!canEdit}
          className={`shrink-0 rounded-full p-0.5 transition-colors ${
            isDone ? "text-emerald-400 hover:text-emerald-300" : "text-zinc-500 hover:text-zinc-200"
          } ${canEdit ? "cursor-pointer" : "cursor-default"}`}
          aria-label={isDone ? "Mark task incomplete" : "Mark task complete"}
          title={isDone ? "Mark incomplete" : "Mark complete"}
        >
          {isDone ? <Check className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
        </button>
        <button
          type="button"
          onClick={() => onEdit(t)}
          className="flex-1 flex items-center gap-4 text-left cursor-pointer"
        >
          <span className={`px-1.5 py-0.5 rounded-sm text-[10px] font-mono uppercase tracking-wide ${pill.bg}`}>
            {pill.label}
          </span>
          <span
            className={`flex-1 text-sm truncate ${
              isDone ? "text-zinc-500 line-through" : "text-zinc-200"
            }`}
          >
            {t.name}
          </span>
          {isDone && (
            <span className="text-[10px] font-mono text-emerald-400/80 uppercase tracking-wide">
              ✓ Done
            </span>
          )}
          {(() => {
            const status = dueDateStatus(t);
            if (status === "late") {
              return (
                <span
                  data-testid={`task-late-badge-${t.id}`}
                  className="text-[10px] font-mono uppercase tracking-wide px-1.5 py-0.5 rounded-sm bg-rose-500/15 text-rose-300 border border-rose-500/40"
                  title={`Due ${t.due_date}`}
                >
                  Late
                </span>
              );
            }
            if (status === "on_time") {
              return (
                <span
                  data-testid={`task-ontime-badge-${t.id}`}
                  className="text-[10px] font-mono uppercase tracking-wide px-1.5 py-0.5 rounded-sm bg-emerald-500/15 text-emerald-300 border border-emerald-500/40"
                  title={`Due ${t.due_date}`}
                >
                  On Time
                </span>
              );
            }
            return null;
          })()}
          {t.call_sheet_id && (
            <span className="text-[10px] font-mono text-amber-500/70 uppercase tracking-wide">
              ↺ Synced
            </span>
          )}
          {t.is_milestone && (
            <span className="text-[10px] font-mono text-violet-300 uppercase tracking-wide">
              ◆ Milestone
            </span>
          )}
          {commentCounts[t.id] > 0 && (
            <span
              data-testid={`task-comment-count-${t.id}`}
              className="text-[10px] font-mono text-zinc-400 flex items-center gap-1"
              title={`${commentCounts[t.id]} comment${commentCounts[t.id] === 1 ? "" : "s"}`}
            >
              <MessageSquare className="w-3 h-3" /> {commentCounts[t.id]}
            </span>
          )}
          {(() => {
            const assignedId = t.assigned_user_id;
            if (!assignedId) return null;
            const isSelf = assignedId === currentUserId;
            if (!canSeeAllAssignees && !isSelf) return null;
            const m = membersById[assignedId];
            const label = m?.name || m?.email || "Assigned";
            return (
              <span
                data-testid={`task-assignee-${t.id}`}
                className="hidden md:inline-flex items-center gap-1.5 text-[11px] text-zinc-300 max-w-[160px]"
                title={`Assigned to ${label}${isSelf ? " (you)" : ""}`}
              >
                <span
                  className="inline-flex items-center justify-center w-5 h-5 rounded-full text-[9px] font-mono font-bold text-zinc-950 shrink-0"
                  style={{ backgroundColor: colorFor(assignedId) }}
                >
                  {initials(m?.name || m?.email || "?")}
                </span>
                <span className="truncate">
                  {isSelf ? "You" : (m?.name || m?.email?.split("@")[0] || "—")}
                </span>
              </span>
            );
          })()}
          <span className="text-xs text-zinc-500 font-mono w-24 text-right">{t.start}</span>
          <span className="text-xs text-zinc-500 font-mono w-12 text-right">
            {formatDuration(t.duration)}
          </span>
          {inConflict && <AlertTriangle className="w-3.5 h-3.5 text-rose-400" />}
          {!inConflict && t.schedule_mode === "locked" && (
            <AlertTriangle className="w-3.5 h-3.5 text-amber-500/70" />
          )}
        </button>
      </div>
    );
  };

  return (
    <div
      data-testid="plan-task-list"
      className="mt-8 border border-zinc-800 rounded-md overflow-hidden"
    >
      <div className="px-5 py-3 border-b border-zinc-800 bg-zinc-950/80">
        <span
          data-testid="plan-task-list-header-count"
          className="text-overline text-zinc-500"
        >
          Task List · {activeTasks.length}
        </span>
      </div>
      {activeTasks.length > 0 ? (
        <div className="divide-y divide-zinc-900">
          {activeTasks.map((t) => renderRow(t))}
        </div>
      ) : (
        <div className="px-5 py-6 text-center text-xs text-zinc-500">
          Everything in this view is complete.
        </div>
      )}

      {doneTasks.length > 0 && (
        <div
          data-testid="plan-completed-section"
          className="border-t border-zinc-800"
        >
          <button
            type="button"
            data-testid="plan-completed-section-toggle"
            onClick={toggleCompleted}
            aria-expanded={completedOpen}
            className="w-full px-5 py-3 flex items-center justify-between gap-2 bg-zinc-950/60 hover:bg-zinc-950 transition-colors text-left"
          >
            <span className="flex items-center gap-2 text-overline text-zinc-500">
              {completedOpen ? (
                <ChevronDown className="w-3.5 h-3.5" />
              ) : (
                <ChevronRight className="w-3.5 h-3.5" />
              )}
              Completed · {doneTasks.length}
            </span>
          </button>

          {completedOpen && (
            <>
              {isAdminRole && (
                <div
                  data-testid="plan-completed-bulk-bar"
                  className="px-5 py-2.5 flex items-center justify-between gap-3 bg-zinc-950/40 border-t border-zinc-900"
                >
                  <label
                    className="inline-flex items-center gap-2 text-xs text-zinc-400 cursor-pointer select-none"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Checkbox
                      data-testid="plan-completed-select-all"
                      checked={allSelected ? true : someSelected ? "indeterminate" : false}
                      onCheckedChange={toggleSelectAll}
                      aria-label="Select all completed tasks"
                      className="border-zinc-700 data-[state=checked]:bg-rose-500 data-[state=checked]:border-rose-500"
                    />
                    Select all
                  </label>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    data-testid="plan-completed-bulk-delete-btn"
                    onClick={() => setConfirmOpen(true)}
                    disabled={selectedIds.size === 0}
                    className="h-8 px-3 rounded-sm bg-transparent border-rose-900/60 text-rose-300 hover:bg-rose-950/40 hover:text-rose-200 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                    Delete selected
                    {selectedIds.size > 0 ? ` (${selectedIds.size})` : ""}
                  </Button>
                </div>
              )}
              <div className="divide-y divide-zinc-900 border-t border-zinc-900">
                {doneTasks.map((t) =>
                  renderRow(t, { withCheckbox: isAdminRole }),
                )}
              </div>
            </>
          )}
        </div>
      )}

      <Dialog open={confirmOpen} onOpenChange={(o) => !deleting && setConfirmOpen(o)}>
        <DialogContent
          data-testid="plan-completed-bulk-delete-confirm"
          className="bg-zinc-950 border-zinc-800 text-zinc-100 sm:max-w-md"
        >
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-light flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-rose-400" />
              Delete {selectedIds.size} completed task
              {selectedIds.size === 1 ? "" : "s"}?
            </DialogTitle>
            <DialogDescription className="text-zinc-500">
              This permanently removes the selected completed task
              {selectedIds.size === 1 ? "" : "s"} and any dependencies attached
              to {selectedIds.size === 1 ? "it" : "them"}. Tasks auto-synced
              from a call sheet day cannot be removed here — delete the day
              instead.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="pt-2">
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={deleting}
              data-testid="plan-completed-bulk-delete-cancel"
              className="bg-transparent border-zinc-800 text-zinc-300 hover:bg-zinc-900"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmBulkDelete}
              disabled={deleting}
              data-testid="plan-completed-bulk-delete-confirm-btn"
              className="bg-rose-600 hover:bg-rose-500 text-white"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  Deleting…
                </>
              ) : (
                <>
                  Delete {selectedIds.size} task
                  {selectedIds.size === 1 ? "" : "s"}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
